import { useState, useEffect, useRef } from "react";
import { FormInput, FormSelect } from "@/components/ui/FormField";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/SearchableSelect";
import type { MutableRefObject } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
  type InviteeFormSchemaType,
  parseRentAmountNumeric,
  RENT_AMOUNT_MIN_SUBMIT_MESSAGE,
} from "../utils/inviteeValidators";
import { getProperties, getPropertyDetails } from "../services/inviteeService";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { showError, formatApiError } from "@/lib/utils/notifications";
import styles from "../styles/inviteeAdd.module.css";
import {
  sanitizeInviteIncomeRawInput,
  formatInviteIncomeWhileTyping,
} from "../utils/inviteeFormatters";
import { RentConfirmationModal } from "./RentConfirmationModal";
import { InviteeValidationMessageModal } from "./InviteeValidationMessageModal";
import { useUserRole } from "@/hooks/useUserRole";
import { LEASE_LEAD_TIME_VALIDATION_HTML } from "../constants/leadTimeValidation";

interface PrimaryApplicantProps {
  form: UseFormReturn<InviteeFormSchemaType>;
  /** Parent sets this on pointerdown over Send Invitation / Apply Now so rent confirm (500–2500) does not open on that blur. */
  skipRentConfirmationOnNextBlurRef?: MutableRefObject<boolean>;
  /** After min-rent modal closes, timestamps (ms) until which rent confirmation blur is suppressed. */
  rentConfirmationSuppressUntilRef?: MutableRefObject<number>;
  onPropertySelect?: (propertyId: string) => void;
  onCheckDuplicate?: () => void;
}

export function PrimaryApplicant({
  form,
  skipRentConfirmationOnNextBlurRef,
  rentConfirmationSuppressUntilRef,
  onPropertySelect,
  onCheckDuplicate,
}: PrimaryApplicantProps) {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
    clearErrors,
  } = form;

  const user = useAppSelector(selectUser);
  const { propertyId: rolePropertyId, propertyName: rolePropertyName } = useUserRole();
  const [properties, setProperties] = useState<SearchableSelectOption[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [, setPropertySearchText] = useState("");
  const [propertyDetails, setPropertyDetails] = useState<any>(null);
  const [showRentModal, setShowRentModal] = useState(false);
  const [pendingRentAmount, setPendingRentAmount] = useState("");
  const [showLeadTimeValidationModal, setShowLeadTimeValidationModal] = useState(false);
  const [isPropertyLocked, setIsPropertyLocked] = useState(false);
  const rolePropertyInitialized = useRef(false);

  const propertyId = watch("property_id");
  const rentAmount = watch("rent_amount");
  const leaseStartDate = watch("lease_start_date");
  const incomeVerified = watch("income_verified");
  const income = watch("income");

  useEffect(() => {
    if (errors.rent_amount?.message === RENT_AMOUNT_MIN_SUBMIT_MESSAGE) {
      setShowRentModal(false);
      setPendingRentAmount("");
    }
  }, [errors.rent_amount?.message]);

  /**
   * Role-based property auto-lock (mirrors Angular getAccessiblePermission + isPropertyListed).
   * If the current role has a property_id, check if the property is listed/active.
   * If active → lock the property dropdown and pre-select it.
   * If not active → lock the property dropdown and show error.
   */
  useEffect(() => {
    if (rolePropertyInitialized.current) return;
    if (!rolePropertyId) return;

    rolePropertyInitialized.current = true;

    const checkRoleProperty = async () => {
      setLoadingProperties(true);
      try {
        const property = await getPropertyDetails(String(rolePropertyId));
        if (property && property.listed === '1') {
          setIsPropertyLocked(true);
          setProperties([{
            value: String(rolePropertyId),
            label: rolePropertyName || property.property_name || String(rolePropertyId),
          }]);
          setValue("property_id", String(rolePropertyId), { shouldValidate: true });
          setPropertyDetails(property);
        } else {
          setIsPropertyLocked(true);
          showError(
            "This property is not active with Liberty. To add invite property must be active with Liberty.",
            false,
          );
        }
      } catch (error: any) {
        setIsPropertyLocked(true);
        showError(formatApiError(error), false);
      } finally {
        setLoadingProperties(false);
      }
    };

    checkRoleProperty();
  }, [rolePropertyId, rolePropertyName, setValue]);

  // Load properties on search (only if property is NOT locked by role)
  const loadProperties = async (search: string) => {
    if (isPropertyLocked) return;
    setLoadingProperties(true);
    try {
      const data = await getProperties(search);
      setProperties(
        data.map((property) => ({
          value: String(property.property_id),
          label: property.property_name || String(property.property_id),
        })),
      );
      // Auto-select if only one property
      if (data.length === 1 && !propertyId) {
        const propId = String(data[0].property_id);
        setValue("property_id", propId, { shouldValidate: true });
        if (onPropertySelect) {
          onPropertySelect(propId);
        }
      }
    } catch (error: any) {
      console.error("Failed to load properties:", error);
      showError(formatApiError(error), false);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Handle property selection (disabled when locked by role)
  const handlePropertyChange = async (value: string | string[]) => {
    if (isPropertyLocked) return;

    const propId = Array.isArray(value) ? value[0] : value;
    if (!propId) {
      setValue("property_id", "", { shouldValidate: true });
      setPropertyDetails(null);
      return;
    }

    const propIdString = String(propId);
    setValue("property_id", propIdString, { shouldValidate: true });
    if (onPropertySelect) {
      onPropertySelect(propIdString);
    }
    try {
      const property = await getPropertyDetails(propIdString);
      setPropertyDetails(property);

      if (property.listed !== "1") {
        showError(
          "This property is not active with Liberty.<br>To add invite property must be active with Liberty.",
          false,
        );
        setValue("property_id", "", { shouldValidate: true });
        setPropertyDetails(null);
      }
    } catch (error: any) {
      console.error("Failed to load property details:", error);
      showError(formatApiError(error), false);
      setValue("property_id", "", { shouldValidate: true });
      setPropertyDetails(null);
    }
  };

  /**
   * Confirm unusual rent (outside $500–$2500) on blur, including amounts ≤ $10.
   * Skipping the next blur after the min-rent validation modal (InviteeAdd) avoids a pop-up loop.
   */
  const validateRentAmount = (value: string) => {
    const n = parseRentAmountNumeric(value);
    if (Number.isNaN(n)) return;
    if (n < 500 || n > 2500) {
      setPendingRentAmount(value);
      setShowRentModal(true);
    }
  };

  const FORM_ACTIONS_SELECTOR = '[data-testid="invitee-add-form-actions"]';

  const handleRentAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      rentConfirmationSuppressUntilRef &&
      Date.now() < rentConfirmationSuppressUntilRef.current
    ) {
      return;
    }
    if (skipRentConfirmationOnNextBlurRef?.current) {
      skipRentConfirmationOnNextBlurRef.current = false;
      return;
    }
    const next = e.relatedTarget as HTMLElement | null;
    const actionsBar = document.querySelector(FORM_ACTIONS_SELECTOR);
    if (next && actionsBar?.contains(next)) {
      return;
    }
    validateRentAmount(rentAmount || "");
  };

  const handleRentConfirm = () => {
    setShowRentModal(false);
  };

  const handleRentCancel = () => {
    setShowRentModal(false);
    setValue("rent_amount", "", { shouldValidate: false });
  };

  // Lease lead time: like Angular – validate when date is entered, show center modal only, block invalid date
  const leadDaysRaw = propertyDetails?.lease_start_lead_days;
  const leadDays =
    leadDaysRaw !== undefined && leadDaysRaw !== null && leadDaysRaw !== ""
      ? Number(leadDaysRaw)
      : 120; // fallback 4 months

  /** Returns true if the given date is beyond company lead time (so we must not accept it). */
  const isLeaseDateBeyondLeadTime = (value: string | null | undefined): boolean => {
    if (!value || leadDays <= 0) return false;
    const selectedDate = new Date(value);
    if (isNaN(selectedDate.getTime())) return false;
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + leadDays);
    return selectedDate > maxDate;
  };

  const handleLeaseStartDateChange = (value: string) => {
    if (value && isLeaseDateBeyondLeadTime(value)) {
      setShowLeadTimeValidationModal(true);
      setValue("lease_start_date", "", { shouldValidate: true });
    } else {
      setValue("lease_start_date", value, { shouldValidate: true });
    }
  };

  /**
   * Re-apply locked property_id after form reset (mirrors Angular moreInvite).
   * When the user clicks "Start a new Invite", form.reset() clears property_id to "".
   * This effect detects that case and re-patches the locked property value.
   */
  useEffect(() => {
    if (isPropertyLocked && rolePropertyId && !propertyId) {
      setValue("property_id", String(rolePropertyId), { shouldValidate: true });
    }
  }, [isPropertyLocked, rolePropertyId, propertyId, setValue]);

  // Initial property load (skip if property is locked by role)
  useEffect(() => {
    if (!rolePropertyId) {
      loadProperties("");
    }
  }, [rolePropertyId]);

  return (
    <>
      <h2
        data-testid="invitee-add-section-primary-applicant-title"
        className={styles.sectionTitle}
      >
        Resident Application Information (Primary Applicant that Liberty needs to approve)
      </h2>

      {/* Applicant Details - First Name and Last Name side by side */}
      <div className={styles.formRow}>
        <div className={`${styles.formCol} ${styles.formColOffset}`}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="first_name"
              data-testid="invitee-add-primary-first-name-label"
            >
              First Name<span className={styles.requiredStar}>*</span>
            </label>
            {(() => {
              const { onBlur: registerOnBlur, ...rest } = register("first_name");
              return (
                <FormInput
                  {...rest}
                  data-testid="invitee-add-primary-first-name-input"
                  placeholder="First Name"
                  error={errors.first_name?.message}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    registerOnBlur(e);
                    void trigger("first_name");
                  }}
                  className={styles.customFormControl}
                />
              );
            })()}
          </div>
        </div>
        <div className={styles.formCol}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="last_name"
              data-testid="invitee-add-primary-last-name-label"
            >
              Last Name<span className={styles.requiredStar}>*</span>
            </label>
            {(() => {
              const { onBlur: registerOnBlur, ...rest } = register("last_name");
              return (
                <FormInput
                  {...rest}
                  data-testid="invitee-add-primary-last-name-input"
                  placeholder="Last Name"
                  error={errors.last_name?.message}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    registerOnBlur(e);
                    void trigger("last_name");
                  }}
                  className={styles.customFormControl}
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* Mobile Phone and Email side by side */}
      <div className={styles.formRow}>
        <div className={`${styles.formCol} ${styles.formColOffset}`}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="mobile_no"
              data-testid="invitee-add-primary-mobile-label"
            >
              Mobile Number (For Text Message)
              <span className={styles.requiredStar}>*</span>
            </label>
            <PhoneInput
              data-testid="invitee-add-primary-mobile-input"
              value={watch("mobile_no") || ""}
              onChange={(value) =>
                setValue("mobile_no", value, { shouldValidate: true })
              }
              onBlur={() => {
                trigger("mobile_no");
                onCheckDuplicate?.();
              }}
              error={errors.mobile_no?.message}
              className={styles.customFormControl}
            />
          </div>
        </div>
        <div className={styles.formCol}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="email"
              data-testid="invitee-add-primary-email-label"
            >
              Email<span className={styles.requiredStar}>*</span> (MUST BE A
              VALID EMAIL ADDRESS)
            </label>
            {(() => {
              const { onBlur: registerOnBlur, onChange: registerOnChange, ...rest } = register("email");
              return (
                <FormInput
                  {...rest}
                  data-testid="invitee-add-primary-email-input"
                  type="text"
                  autoComplete="email"
                  placeholder="Email"
                  error={errors.email?.message}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.toLowerCase();
                    registerOnChange(e);
                    void trigger("email");
                  }}
                  onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                    registerOnBlur(e);
                    const isValidEmail = await trigger("email");
                    if (isValidEmail) {
                      onCheckDuplicate?.();
                    }
                  }}
                  className={styles.customFormControl}
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* Privacy Note */}
      <div className={styles.formRow}>
        <div
          className={styles.formColWide}
        >
          <div
            data-testid="invitee-add-primary-privacy-note"
            className={styles.privacyNote}
          >
            Data Privacy Note: Liberty Rent will contact the applicants by Text
            Msg to the Phone # or Email address provided, only so that we can
            communicate the status of your application with our program. We do
            not share this information with any outside parties. Please add
            Liberty Rent to your contacts, so we are not blocked.
          </div>
        </div>
      </div>

      {/* Property and Unit */}
      <div className={styles.formRow}>
        <div className={`${styles.formCol} ${styles.formColOffset}`}>
          <div
            className={styles.formGroup}
            style={isPropertyLocked ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
          >
            <label
              className={styles.formLabel}
              htmlFor="property_id"
              data-testid="invitee-add-primary-property-label"
            >
              Property Name<span className={styles.requiredStar}>*</span>
            </label>
            <SearchableSelect
              triggerDataTestId="invitee-add-primary-property-select-trigger"
              options={properties}
              value={propertyId ? String(propertyId) : undefined}
              onChange={handlePropertyChange}
              placeholder="All Properties"
              searchPlaceholder="Search By Property Name, City, State"
              onSearch={(search) => {
                if (!isPropertyLocked) {
                  setPropertySearchText(search);
                  loadProperties(search);
                }
              }}
              loading={loadingProperties}
              error={errors.property_id?.message}
              className={styles.customFormControl}
              disabled={isPropertyLocked}
            />
          </div>
        </div>
        <div className={styles.formCol}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="unit_number"
              data-testid="invitee-add-primary-unit-label"
            >
              Unit Number (optional)
            </label>
            <FormInput
              {...register("unit_number")}
              data-testid="invitee-add-primary-unit-input"
              placeholder="Unit Number"
              maxLength={100}
              error={errors.unit_number?.message}
              className={styles.customFormControl}
            />
          </div>
        </div>
      </div>

      {/* Rent Amount, Lease Start Date, Lease Duration */}
      <div className={styles.formRow}>
        <div className={`${styles.formCol} ${styles.formColOffset}`}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="rent_amount"
              data-testid="invitee-add-primary-rent-label"
            >
              Rent Amount<span className={styles.requiredStar}>*</span>
            </label>
            <CurrencyInput
              id="rent_amount"
              data-testid="invitee-add-primary-rent-input"
              value={rentAmount || ""}
              onChange={(value) => {
                clearErrors("rent_amount");
                setShowRentModal(false);
                // Min-rent (> $10): Apply/Send only. 500–2500 confirm: blur (see validateRentAmount).
                setValue("rent_amount", value, { shouldValidate: false });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && skipRentConfirmationOnNextBlurRef) {
                  skipRentConfirmationOnNextBlurRef.current = true;
                }
              }}
              onBlur={handleRentAmountBlur}
              error={errors.rent_amount?.message}
              className={styles.customFormControl}
            />
          </div>
        </div>
        <div className={styles.formCol}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="lease_start_date"
              data-testid="invitee-add-primary-lease-start-label"
            >
              Lease Start Date<span className={styles.requiredStar}>*</span>
            </label>
            <DatePicker
              dataTestId="invitee-add-primary-lease-start-date"
              value={leaseStartDate || ""}
              onChange={handleLeaseStartDateChange}
              placeholder="MM/DD/YYYY"
              minDate={new Date()}
              maxDate={new Date("2099-12-30")}
              allowFutureDates
              error={errors.lease_start_date?.message}
              className={styles.customFormControl}
            />
          </div>
        </div>
        <div className={styles.formColSmall}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              htmlFor="lease_duration"
              data-testid="invitee-add-primary-lease-duration-label"
            >
              Lease Duration
            </label>
            <FormSelect
              {...register("lease_duration")}
              data-testid="invitee-add-primary-lease-duration-select"
              error={errors.lease_duration?.message}
              className={styles.customFormControl}
            >
              <option value="12">12 months</option>
            </FormSelect>
          </div>
        </div>
      </div>

      {/* Income Verification */}
      <div
        data-testid="invitee-add-primary-income-verification-section"
        className={styles.formRow}
      >
        <div className={`${styles.formColWide} ${styles.formColWideOffset}`}>
          <div>
            <span
              className={styles.formLabel}
              data-testid="invitee-add-primary-income-verification-label"
            >
              Income Verification
              <span className={styles.requiredStar}>*</span>
            </span>
          </div>
          <div className={styles.incomeVerificationContainer}>
            <div className={styles.incomeRadioOption}>
              <div className={styles.incomeRadioLabel}>
                <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
                  <input
                    type="radio"
                    name="income_verified"
                    value="1"
                    checked={incomeVerified === "1"}
                    onChange={() => {
                      setValue("income_verified", "1", { shouldValidate: true });
                    }}
                    className={styles.radioInput}
                    data-testid="invitee-add-primary-income-verified-yes-radio"
                  />
                  <span className={styles.incomeRadioText}>
                    Monthly Income verified as
                  </span>
                </label>
                <div
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "baseline",
                    marginLeft: "4px",
                    marginRight: "4px",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: "4px",
                      fontSize: "13px",
                      color: "#253c5c",
                      pointerEvents: "none",
                      zIndex: 1,
                      top: "50%",
                      transform: "translateY(-50%)",
                      lineHeight: "1",
                    }}
                  >
                    $
                  </span>
                  <input
                    type="text"
                    value={formatInviteIncomeWhileTyping(
                      sanitizeInviteIncomeRawInput(income || ""),
                    )}
                    onChange={(e) => {
                      const raw = sanitizeInviteIncomeRawInput(e.target.value);
                      setValue("income", raw, { shouldValidate: true });
                      setValue("income_verified", "1", {
                        shouldValidate: true,
                      });
                    }}
                    placeholder="Income Total"
                    disabled={false}
                    className={styles.incomeInput}
                    style={{ paddingLeft: "16px" }}
                    data-testid="invitee-add-primary-income-input"
                  />
                </div>
                <span className={styles.incomeRadioText}>(approximately)</span>
              </div>
            </div>
            <div className={styles.incomeRadioOption}>
              <div className={styles.incomeRadioLabel}>
                <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", margin: 0 }}>
                  <input
                    type="radio"
                    name="income_verified"
                    value="0"
                    checked={incomeVerified === "0"}
                    onChange={() => {
                      setValue("income_verified", "0", { shouldValidate: true });
                      setValue("income", "", { shouldValidate: true });
                    }}
                    className={styles.radioInput}
                    data-testid="invitee-add-primary-income-verified-no-radio"
                  />
                  <span className={styles.incomeRadioText}>Not Verified</span>
                </label>
              </div>
            </div>
          </div>
          {errors.income_verified && (
            <div
              data-testid="invitee-add-primary-income-verified-error"
              className={styles.errorMessage}
            >
              <i className="fa fa-exclamation-circle fa-lg" />
              {errors.income_verified.message}
            </div>
          )}
          {errors.income && (
            <div
              data-testid="invitee-add-primary-income-error"
              className={styles.errorMessage}
            >
              <i className="fa fa-exclamation-circle fa-lg" />
              {errors.income.message}
            </div>
          )}
        </div>
      </div>

      {/* Invitation By */}
      <div className={styles.formRow}>
        <div className={`${styles.formCol} ${styles.formColOffset}`}>
          <div className={styles.formGroup}>
            <label
              className={styles.formLabel}
              data-testid="invitee-add-primary-invitation-by-label"
            >
              <b>
                Invitation by: {user?.first_name} {user?.last_name}
              </b>
            </label>
          </div>
        </div>
      </div>

      <RentConfirmationModal
        isOpen={showRentModal}
        onClose={handleRentCancel}
        onConfirm={handleRentConfirm}
        amount={pendingRentAmount}
      />

      {/* Validation Message – lease start date beyond company lead time (matches Angular dialog exactly) */}
      <InviteeValidationMessageModal
        isOpen={showLeadTimeValidationModal}
        message={LEASE_LEAD_TIME_VALIDATION_HTML}
        onClose={() => setShowLeadTimeValidationModal(false)}
        overlayDataTestId="invitee-add-primary-lease-lead-time-modal"
        closeButtonDataTestId="invitee-add-primary-lease-lead-time-modal-close-button"
        okButtonDataTestId="invitee-add-primary-lease-lead-time-modal-ok-button"
      />
    </>
  );
}
